import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from "../modes.module.css";
import AITutor from '../AITutor/AITutor';
import BackButton from '../common/BackButton/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const StoryMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [story, setStory] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [completingSession, setCompletingSession] = useState(false);
  const [storyContent, setStoryContent] = useState([]);

  const navigate = useNavigate();
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.story || 1;

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      fetchStory();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (story?.questions?.length > 0) {
      setQuestionStartTime(Date.now());
      setSelectedSentence(null); // Reset selection when question changes
      
      // Split the story content into sentences for selection
      const sentences = splitIntoSentences(story.story.content);
      setStoryContent(sentences);
    }
  }, [currentQuestion, story]);

  // Function to split story into sentences
  const splitIntoSentences = (text) => {
    if (!text) return [];
    
    // Split by sentence endings (., !, ?) followed by a space or end of string
    // This is a simplified approach and might need refinement for complex texts
    // const sentenceRegex = /[^.!?]+[.!?]+\s*/g;
    const sentenceRegex = /[^.?!]+[.!?]+[\])'"`’”]*|.+/g;
    const sentences = text.match(sentenceRegex) || [];
    
    return sentences.map(sentence => sentence.trim());
  };

  const fetchStory = async () => {
    try {
      const data = await apiRequest(`/stories?difficulty=${difficulty}`);
      setStory(data);

      // Split the story content into sentences
      const sentences = splitIntoSentences(data.story.content);
      setStoryContent(sentences);

      // Start new session
      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'story',
          difficulty: difficulty,
          questions: data.story.questions
        })
      });

      localStorage.setItem('currentSessionId', sessionData._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle sentence selection (just highlights, doesn't submit)
  const handleSentenceSelect = (index) => {
    if (isSubmitting || showExplanation) return;
    setSelectedSentence(index);
  };

  // Handle answer submission
  const handleSubmitAnswer = async () => {
    if (isSubmitting || showExplanation || selectedSentence === null) return;
    
    setSubmittingAnswer(true);
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = story.questions[currentQuestion];
      const sentence = storyContent[selectedSentence];
      
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const result = await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: sentence,
          timeSpent
        })
      });

      setAnswers(prev => ({ ...prev, [currentQuestion]: {
        sentence,
        index: selectedSentence,
        isCorrect: result.isCorrect
      }}));
      setShowExplanation(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
      setSubmittingAnswer(false);
    }
  };

  const handleNext = () => {
    setShowExplanation(false);
    setSelectedSentence(null);
    
    if (currentQuestion + 1 >= story.questions.length) {
      handleCompletion();
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleCompletion = async () => {
    setCompletingSession(true);
    
    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const results = await apiRequest(`/sessions/${sessionId}/end`, {
        method: 'POST'
      });

      localStorage.removeItem('currentSessionId');
      updateProgress(results);
      
      // Navigate to results page with session results and questions
      navigate('/results', { 
        state: { 
          sessionResults: results,
          questions: story.questions
        } 
      });
    } catch (err) {
      setError(err.message);
      setCompletingSession(false);
    }
  };

  if (loading || userLoading || !story) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  const currentQ = story.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / story.questions.length) * 100;
  const currentAnswer = answers[currentQuestion];

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      
      <div className={styles.card}>
        <AITutor 
          currentQuestion={story.questions[currentQuestion]}
          userAnswer={currentAnswer?.sentence}
          isCorrect={currentAnswer?.isCorrect}
          storyContext={{
            title: story.title,
            content: story.content,
            grade: story.grade
          }}
        />
        <div className={styles.header}>
          <h2 className={styles.titleStory}>{story.title}</h2>
          <span className={styles.questionCount}>
            Question {currentQuestion + 1} of {story.questions.length}
          </span>
        </div>

        <div className={styles.progressContainer}>
          <div 
            className={styles.progressBar} 
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={styles.questionTextStory}>
          {currentQ.content.text}
        </div>

        <div className={styles.storyContent}>
          {storyContent.map((sentence, index) => (
            <span 
              key={index}
              className={`${styles.sentenceSpan} 
                ${selectedSentence === index ? styles.selectedSentence : ''} 
                ${showExplanation && currentAnswer?.index === index && currentAnswer?.isCorrect ? styles.correctSentence : ''}
                ${showExplanation && currentAnswer?.index === index && !currentAnswer?.isCorrect ? styles.incorrectSentence : ''}
                ${showExplanation && 
                  sentence.replace(/[\s\p{P}]+/gu, '').toLowerCase()
                  .includes(
                    currentQ.content.correctAnswer.replace(/[\s\p{P}]+/gu, '').toLowerCase()
                  ) 
                  ? styles.correctSentence 
                  : ''}
              `}
              onClick={() => handleSentenceSelect(index)}
            >
              {sentence}{' '}
            </span>
          ))}
        </div>

        {!showExplanation && (
          <div className={styles.actionContainer}>
            <button 
              className={`${styles.checkAnswerButton} ${selectedSentence === null ? styles.disabled : ''}`}
              onClick={handleSubmitAnswer}
              disabled={selectedSentence === null || isSubmitting}
            >
              {submittingAnswer ? 'Checking...' : 'Check Answer'}
            </button>
          </div>
        )}

        {showExplanation && (
          <div className={styles.explanationContainer}>
            <div className={styles.explanation}>
              <h3 className={styles.explanationTitle}>
                {currentAnswer?.isCorrect ? 'Correct!' : 'Not quite right'}
              </h3>
              <p className={styles.explanationText}>
                {currentQ.content.explanation}
              </p>
              <div className={styles.correctAnswerHighlight}>
                <strong>Sentence needing correction:</strong> {currentQ.content.correctAnswer}
              </div>
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {currentQuestion + 1 >= story.questions.length ? 'Finish' : 'Next Question'}
            </button>
          </div>
        )}

        <BackButton />
      </div>
    </div>
  );
};

export default StoryMode;